<template>
  <div class="staff-increment-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">Staff Increment List</h4>
        <b-badge v-show="!loading" variant="light-success" class="ml-2">
          {{ reportItems.length }} Staff Members
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print Report</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem" />
      <p class="mt-3 font-weight-bold">Loading increment records...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" @click="loadReportData" class="mt-3"
        >Retry</b-button
      >
    </div>

    <!-- Report Content -->
    <div v-else class="report-page">
      <!-- Modern Header -->
      <div class="report-header-modern mb-4">
        <div class="d-flex align-items-center">
          <div class="logo-wrapper mr-3" v-if="schoolLogo">
            <img :src="schoolLogo" alt="Logo" class="school-logo-img" />
          </div>
          <div class="header-text-block">
            <h2 class="school-name-full mb-0">
              {{ campusName
              }}<span v-if="campusBranch" class="text-muted">
                - {{ campusBranch }}</span
              >
            </h2>
            <div class="report-info-line mt-1">
              <span class="report-name-text">Staff Increment List</span>
              <span class="date-range-text">
                <strong
                  >Date / Year:
                  {{
                    isAlhameedDomain ? queryParams.year : queryParams.dtRange
                  }}</strong
                >
                <strong class="mx-2">|</strong>
                <strong>Generated on {{ currentDateTime }}</strong>
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Data Table -->
      <div class="table-container">
        <table class="ledger-style-table">
          <thead>
            <tr>
              <th class="text-center" style="width: 3%">S.No</th>
              <th class="text-center" style="width: 8%">RF ID</th>
              <th class="text-left" style="width: 20%">STAFF NAME</th>
              <th class="text-left" style="width: 20%">FATHER NAME</th>
              <th class="text-left" style="width: 15%">DESIGNATION</th>
              <th class="text-left" style="width: 15%">DEPARTMENT</th>
              <th class="text-right" style="width: 9%">BASIC SALARY</th>
              <th class="text-right" style="width: 10%">INCREMENT AMOUNT</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in reportItems" :key="index">
              <td class="text-center font-weight-bold">{{ item.sno }}</td>
              <td class="text-center id-cell">
                {{ item.rfID || "-" }}
              </td>
              <td class="text-left font-weight-bold">
                {{ (item.staffName || "-").toUpperCase() }}
              </td>
              <td class="text-left">
                {{ (item.fatherName || "-").toUpperCase() }}
              </td>
              <td class="text-left">
                {{ item.designation || "-" }}
              </td>
              <td class="text-left">
                {{ item.department ? item.department.name : "-" }}
              </td>
              <td class="text-right font-weight-bold">
                {{ formatCurrency(item.basicSalary) }}
              </td>
              <td class="text-right font-weight-bold text-success">
                {{
                  isAlhameedDomain
                    ? formatCurrency(item.totalIncrementAmount)
                    : item.totalIncrementAmount || "0"
                }}
              </td>
            </tr>
            <tr v-if="reportItems.length === 0">
              <td colspan="8" class="text-center py-5 no-data">
                <feather-icon
                  icon="InboxIcon"
                  size="32"
                  class="text-muted mb-2"
                />
                <p>No increment records found for the selected criteria</p>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="reportItems.length > 0">
             <tr class="grand-total-row">
                <td colspan="7" class="text-right font-weight-bold">Total Increment Amount:</td>
                <td class="text-right font-weight-bold text-success" style="font-size: 1.1em;">
                  {{ isAlhameedDomain ? formatCurrency(totalIncrementAmountAlhameed) : "" }}
                </td>
             </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";

export default {
  name: "StaffIncrementListPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      campusName: "MY SKOOL",
      campusBranch: "",
      schoolLogo: null,
      queryParams: {},
      isAlhameedDomain: false,
    };
  },
  computed: {
    currentDateTime() {
      const now = new Date();
      return now.toLocaleString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },
    totalIncrementAmountAlhameed() {
      if (!this.reportItems) return 0;
      return this.reportItems.reduce((acc, item) => acc + (Number(item.totalIncrementAmount) || 0), 0);
    }
  },
  created() {
    this.initReport();
  },
  methods: {
    formatCurrency(value) {
      if (value === undefined || value === null || isNaN(value)) return '0';
      return new Intl.NumberFormat('en-PK', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(value);
    },
    initReport() {
      const query = this.$route.query;
      this.queryParams = {
        db: query.db || this.$store.state.userData.db || "",
        cID: Number(query.cID || 0),
        depID: Number(query.depID || 0),
        year: query.year || "",
        dtFrom: query.dtFrom || "",
        dtTo: query.dtTo || "",
        dtRange: query.dtFrom && query.dtTo ? `${query.dtFrom} to ${query.dtTo}` : "",
      };

      this.isAlhameedDomain = query.domain === "alhameed";

      if (!this.queryParams.db) {
        this.error =
          "Database identifier is missing. Please close and try again.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = `https://apiv26.myskool.app/api/campuses`;
        const response = await axios.get(url, {
          params: { db: this.queryParams.db, cId: this.queryParams.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find(
            (c) => c.id == this.queryParams.cID
          );
          if (match) {
            this.campusName = match.name;
            this.campusBranch =
              this.queryParams.cID == 0 ? "All Campuses" : match.branch;
            this.schoolLogo = match.logo;
          }
        }
      } catch (e) {
        console.warn("Campus info fetch failed, using fallback from store");
        const branches = this.$store.state.branches || [];
        const currentBranch =
          branches.find((b) => b.id == this.queryParams.cID) ||
          this.$store.state.currentBranch;
        if (currentBranch) {
          this.campusName =
            currentBranch.organizationName ||
            currentBranch.branch ||
            "MY SKOOL";
          this.campusBranch = currentBranch.branch || "";
        }
      }
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        let apiUrl = "";
        if (this.isAlhameedDomain) {
          apiUrl = `https://apiv26.myskool.app/api/Staff/IncrementReport?db=${this.queryParams.db}&cID=${this.queryParams.cID}&year=${this.queryParams.year}&depID=${this.queryParams.depID}`;
        } else {
          apiUrl = `https://apiv26.myskool.app/api/Staff/IncrementReport?db=${encodeURIComponent(
            this.queryParams.db
          )}&cID=${this.queryParams.cID}&depID=${this.queryParams.depID}&dtFrom=${encodeURIComponent(
            this.queryParams.dtFrom
          )}&dtTo=${encodeURIComponent(this.queryParams.dtTo)}`;
        }

        const response = await axios.get(apiUrl, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        let dataArray = [];
        if (Array.isArray(response.data)) {
          dataArray = response.data;
        } else if (response.data && Array.isArray(response.data.data)) {
          dataArray = response.data.data;
        }

        if (dataArray && dataArray.length > 0) {
          let reportData = dataArray.map((item, index) => {
            let yearWiseIncrements = "";
            if (
              !this.isAlhameedDomain &&
              item.incrementDetails &&
              item.incrementDetails.length > 0
            ) {
              const yearGroups = {};
              item.incrementDetails.forEach((detail) => {
                const year = new Date(detail.incrementDate).getFullYear();
                if (!yearGroups[year]) yearGroups[year] = 0;
                yearGroups[year] += detail.incrementAmount || 0;
              });

              yearWiseIncrements = Object.keys(yearGroups)
                .sort()
                .map((year) => yearGroups[year])
                .join(", ");
            }

            return {
              sno: index + 1,
              rfID: item.rfID || "N/A",
              staffName: item.staffName || "N/A",
              fatherName: item.fatherName || "N/A",
              designation: item.designation || "N/A",
              department: item.department || {
                name: item.departmentName || "N/A",
              },
              basicSalary: item.basicSalary || 0,
              totalIncrementAmount: this.isAlhameedDomain
                ? item.totalIncrementAmount || 0
                : yearWiseIncrements,
            };
          });

          if (this.isAlhameedDomain) {
            const designationOrder = [
              "SEC. INCH.(BOYS)",
              "Dy. ADM.",
              "NUR.INCH",
              "INCHARGE",
              "Incharge",
              "COOR",
              "COOR. NUR.",
              "COOR. KG-II",
              "COOR. KG-I",
              "COOR. CLS-II",
              "COOR. CLS-I",
              "COOR. VII-VIII",
              "COOR. IX-X",
              "LOW. SEC. INCH.",
              "PRI.INCH.",
              "PRIN.",
              "SEC. INCH.(GIRLS)",
              "COOR. ADM.",
              "RECEP.",
              "ASST. ACCT",
              "CSHR & ASST. ACCT",
              "CASHIER",
              "STK. INCH.",
              "ASST. STK. INCH.",
              "COMP. LAB. ATT",
              "SCI. LAB. ATT",
              "SEC. OFCR.",
              "GRA. DES",
              "ELECTRICIAN",
              "BND. MAS.",
              "Teacher",
              "P.P.S.T",
              "P.S.T",
              "P.P.S.T(CO)",
              "QARIA",
              "J.S.T",
              "MED CONSL.",
              "H.S.T",
              "MALE PEON",
              "LADY PEON",
              "CLEANING STAFF",
              "D/N WATCH MAN",
              "DRIVER",
              "Co - teacher",
              "GEN. OPR",
              "MUS. TCHR.",
            ];

            reportData.sort((a, b) => {
              const findDesignationIndex = (designation) => {
                let index = designationOrder.indexOf(designation);
                if (index !== -1) return index;

                index = designationOrder.findIndex(
                  (d) => d.toLowerCase() === designation.toLowerCase()
                );
                if (index !== -1) return index;

                const normalizedDesignation = designation.toLowerCase().trim();
                if (normalizedDesignation.includes("incharge")) {
                  index = designationOrder.findIndex((d) =>
                    d.toLowerCase().includes("incharge")
                  );
                  if (index !== -1) return index;
                }
                if (normalizedDesignation.includes("coordinator")) {
                  index = designationOrder.findIndex((d) =>
                    d.toLowerCase().includes("coordinator")
                  );
                  if (index !== -1) return index;
                }

                const cleanDesignation = designation
                  .replace(/[().]/g, "")
                  .toLowerCase();
                index = designationOrder.findIndex(
                  (d) =>
                    d.replace(/[().]/g, "").toLowerCase() === cleanDesignation
                );
                if (index !== -1) return index;

                return -1;
              };

              const aIndex = findDesignationIndex(a.designation);
              const bIndex = findDesignationIndex(b.designation);

              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }

              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;

              return a.designation.localeCompare(b.designation);
            });

            reportData.forEach((item, index) => {
              item.sno = index + 1;
            });
          }

          this.reportItems = reportData;
        } else {
          this.reportItems = [];
        }
      } catch (err) {
        console.error("Error fetching increment report:", err);
        this.error =
          "Failed to retrieve report data. Please ensure the API endpoint is accessible.";
      } finally {
        this.loading = false;
      }
    },
    print() {
      setTimeout(() => {
        window.print();
      }, 500);
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
.staff-increment-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 2.5rem;
  font-family: "Outfit", "Inter", sans-serif;
  color: #2d3436;
}

.control-bar {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.3);

  h4 {
    color: #444;
    font-weight: 700;
    font-size: 1.25rem;
    letter-spacing: -0.5px;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);

  span {
    margin-left: 4px;
  }

  &:active {
    transform: scale(0.96);
  }
}

.btn-print {
  background: linear-gradient(135deg, #6c5ce7 0%, #a29bfe 100%);
  color: white;

  &:hover {
    background: linear-gradient(135deg, #5b4cc4 0%, #8c82eb 100%);
    box-shadow: 0 6px 20px rgba(108, 92, 231, 0.35);
    transform: translateY(-1px);
  }
}

.btn-close {
  background: white;
  color: #636e72;
  border: 1px solid #dfe6e9;

  &:hover {
    background: #fdfdfd;
    color: #e74c3c;
    border-color: #fab1a0;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.05);
    transform: translateY(-1px);
  }
}

.report-page {
  background: #ffffff;
  padding: 3rem 2.5rem;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  width: 98%;
  max-width: 1200px;
  margin: 30px auto;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.report-header-modern {
  border-bottom: 2px solid #333;
  padding-bottom: 20px;

  .school-logo-img {
    height: 130px;
    width: auto;
    object-fit: contain;
  }

  .school-name-full {
    font-size: 2rem;
    font-weight: 800;
    color: #000;
    text-transform: uppercase;
    letter-spacing: -0.5px;
  }

  .report-info-line {
    font-size: 1.1rem;
    color: #444;
    display: flex;
    align-items: center;

    .report-name-text {
      font-weight: 700;
      border-right: 2px solid #ddd;
      padding-right: 15px;
      margin-right: 15px;
    }

    .date-range-text {
      color: #555;
    }
  }
}

.ledger-style-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  border: 1px solid #dee2e6;
  background: white;

  th,
  td {
    border: 1px solid #dee2e6;
    padding: 12px 15px;
    box-sizing: border-box;
    font-size: 0.95rem;
  }

  thead {
    background: #2c3e50 !important;
    th {
      color: white !important;
      font-weight: 800;
      text-transform: uppercase;
      font-size: 0.9rem;
      letter-spacing: 0.5px;
    }
  }
  
  tfoot {
    background: #f8f9fa;
  }

  tbody tr:nth-child(even) {
    background-color: #f8f9fa;
  }

  tbody tr:hover {
    background: #f1f4ff;
  }

  .id-cell {
    font-weight: 700;
    color: #6c5ce7;
  }
}

@page {
  size: A4 portrait;
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
    color: #000 !important;
  }

  .staff-increment-report {
    background: white !important;
    padding: 0 !important;
  }

  .report-page {
    box-shadow: none !important;
    border: none !important;
    padding: 1.2cm 0.8cm !important;
    max-width: 100% !important;
    width: 100% !important;
    margin: 0 !important;
  }

  .ledger-style-table {
    font-size: 9pt !important;
    width: 100% !important;

    th,
    td {
      padding: 6px 10px !important;
      border: 1px solid #000 !important;
      color: #000 !important;
    }

    thead th {
      background: #f1f2f6 !important;
      color: #000 !important;
      font-weight: 800 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }

  .report-header-modern {
    margin-bottom: 25px !important;
    padding-bottom: 15px !important;
    border-bottom: 4px solid #000 !important;
    .school-logo-img {
      height: 140px !important;
      max-width: 350px !important;
      object-fit: contain !important;
    }
    .school-name-full {
      font-size: 1.8rem !important;
    }
  }
}
</style>
